























































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { ICoupon, IValidations } from '@/interfaces';
import { AjaxHelper } from '@/AjaxHelper';
import { CouponValidator } from '@/validators';

import List from '@/components/List.vue';
import ListItem from '@/components/ListItem.vue';
import Btn from '@/components/Btn.vue';
import Field from '@/components/Field.vue';
import TextField from '@/components/TextField.vue';
import NumberField from '@/components/NumberField.vue';
import BtnGroup from '@/components/BtnGroup.vue';
import SliderField from '@/components/SliderField.vue';

@Component({
  name: 'admin-coupon-page',
  components: { List, ListItem, Btn, Field, TextField, BtnGroup, NumberField, SliderField },
  beforeRouteEnter: async (to, from, next) => {
    const coupons = await AjaxHelper.get(`/coupon?showDisabled=false`);
    next(vm => {
      (vm as any).coupons = coupons;
    });
  }
})
export default class AdminCouponComponent extends Vue {
  /**
   * The list of coupons that exist in the system
   */
  public coupons: ICoupon[] = [];

  /**
   * If the coupons are being saved or not
   */
  public saving = false;

  /**
   * If we should show disabled or not
   */
  public showDisabled = false;

  /**
   * The index that is currently being updated
   */
  public updatingCoupon: ICoupon | null = null;

  /**
   * The search text we want to find
   */
  public searchText = '';

  /**
   * If we should show the validations on the page or not
   */
  public modelShowValidations = false;

  /**
   * The list of valid coupons in the current list
   */
  public get couponValidations(): (IValidations<ICoupon> | null)[] { return this.getCouponValidations(); }

  /**
   * Validator function to check if a coupon value is valid or not
   */
  public couponValidator = new CouponValidator();

  /**
   * Getter to filter out coupons based on the search critiera
   */
  public get filteredCoupons(): ICoupon[] {
    if (this.searchText.length === 0) return this.coupons;
    return this.coupons
      .filter(x =>
        x.name.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1 ||
        x.code.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1);
  }

  /**
   * If we are dealing with new coupons or no
   */
  public get hasNewCoupons(): boolean { return this.coupons.find(x => x.id === undefined) !== undefined; }

  /**
   * Helper method is meant to add a blank coupon to the system for editing
   */
  public addCoupon() {
    this.coupons.push({ isActive: true, name: '', code: '', number: 0, isPercentage: false })
  }

  /**
   * Helper callback to save all the newly created coupons in the system
   */
  public async saveCoupons() {
    try {
      this.saving = true;
      const validations = this.getCouponValidations();
      if (validations.filter(x => x !== null).length === 0) {
        this.modelShowValidations = false;

        for (let i = 0; i < this.coupons.length; ++i) {
          try {
            if (this.coupons[i].id === undefined) {
              this.coupons[i] = await AjaxHelper.post<ICoupon, ICoupon>('/coupon', this.coupons[i]);
            }
          } catch (ex) {
            console.log({ ex });
          }
        }

        this.$alert.success('Successfully Add Coupons');
        this.$forceUpdate();
      } else {
        this.modelShowValidations = true;
      }
    } catch {
      this.$alert.danger('Error Occured While Saving Coupons');
    } finally {
      this.saving = false;
    }
  }

  /**
   * Helper method is meant to enable a coupon in the database
   * 
   * @param coupon The coupon we are currently trying to enable
   */
  public async enable(coupon: ICoupon) {
    if (coupon.id !== undefined) {
      try {
        this.updatingCoupon = coupon;
        const updatingIndex = this.coupons.indexOf(coupon);
        if (updatingIndex > -1) {
          this.coupons[updatingIndex] = await AjaxHelper.put(`/coupon/${coupon.id}/enable`);
          this.$alert.success('Successfully Enabled Coupon');
          this.$forceUpdate();
        } else {
          this.$alert.warning('Coupon could not be found');
        }
      } catch {
        this.$alert.danger('Error Occured While Enabling Coupon');
      } finally {
        this.updatingCoupon = null;
      }
    }
  }

  /**
   * Helper method is meant to disable a coupon in the database
   * 
   * @param coupon The coupon we are currently trying to disable
   */
  public async disable(coupon: ICoupon) {
    if (coupon.id !== undefined) {
      try {
        this.updatingCoupon = coupon;
        const updatingIndex = this.coupons.indexOf(coupon);
        if (updatingIndex > -1) {
          this.coupons[updatingIndex] = await AjaxHelper.put(`/coupon/${coupon.id}/disable`);
          this.$alert.success('Successfully Disabled Coupon');
          this.$forceUpdate();
        } else {
          this.$alert.warning('Coupon could not be found');
        }
      } catch {
        this.$alert.danger('Error Occured While Disabling Coupon');
      } finally {
        this.updatingCoupon = null;
      }
    }
  }

  /**
   * Helper method to get the validation text that should be displayed to the user when the validation is triggered
   * 
   * @param key The key we want to get the validations from
   */
  public getValidationText(item: ICoupon, key: keyof ICoupon) {
    const index = this.coupons.findIndex(x => x === item);
    if (index > -1) {
      const validations = this.couponValidations[index];
      return (validations !== null ? validations[key] : null) ?? '';
    }
    return '';
  }

  /**
   * Watcher method to load coupons based on whtn the disabled changes
   * 
   * @param newShowDisabled The new disabled value
   */
  @Watch('showDisabled')
  public async onShowDisabledChanged(newShowDisabled: boolean) {
    await this.loadCoupons(newShowDisabled);
  }

  /**
   * Load coupons from the system
   * 
   * @param showDisabled If we should load disabled coupons as well
   */
  private async loadCoupons(showDisabled: boolean) {
    this.coupons = await AjaxHelper.get(`/coupon?showDisabled=${showDisabled}`);
  }

  /**
   * Helper method is meant to get the validations from the coupons
   */
  private getCouponValidations() {
    return this.coupons.map(x => this.modelShowValidations ? this.generateValidations(x) : null) ?? [];
  }

  /**
   * Helper method to generate validations and return null or the validations for use by the component
   * 
   * @param session The current session we are handling
   */
  private generateValidations(session: ICoupon) {
    const validations = this.couponValidator.isValid(session);
    return validations === true ? null : validations;
  }
}
